import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  type AdminCoupon,
  type AdminCouponCatalog,
  type AdminCreateCouponRequest,
  adminCreateCouponRequestSchema,
} from "@ternary/contracts"
import {
  Button,
  type Column,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Field,
  FormWizard,
  IconButton,
  Input,
  MultiSelect,
  PageHeader,
  Radio,
  SearchInput,
  Select,
  Table,
  useToast,
  type WizardErrors,
  WizardFieldError,
  type WizardStep,
  type WizardStepContext,
} from "@ternary/ui"
import { type ReactElement, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { createAdminCoupon, deleteAdminCoupon, loadAdminCoupons } from "../api.js"
import { CommerceTabs, PaymentSettingsLink } from "../components/commerce-tabs.js"
import { DocsLink } from "../components/docs-link.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { NavIcon } from "../components/nav-icons.js"
import { useErrorsUntilEdited } from "../components/setup-page.js"
import { currencySymbol } from "../product-draft.js"

const commonCurrencies = ["USD", "CAD", "EUR", "GBP", "AUD", "JPY"]

function supportedCurrencies() {
  try {
    return Intl.supportedValuesOf("currency")
  } catch {
    return commonCurrencies
  }
}

function couponMatches(coupon: AdminCoupon, search: string) {
  const query = search.trim().toLowerCase()
  return (
    query.length === 0 ||
    [
      coupon.name,
      coupon.id,
      coupon.type,
      coupon.duration,
      coupon.currency,
      ...coupon.appliesToProductIds,
    ].some((value) => value.toLowerCase().includes(query))
  )
}

function discount(
  coupon: Pick<AdminCoupon, "amountOffMinor" | "currency" | "percentOff" | "type">,
) {
  if (coupon.type === "percent") {
    return `${coupon.percentOff}%`
  }
  return new Intl.NumberFormat("en-US", {
    currency: coupon.currency,
    style: "currency",
  }).format((coupon.amountOffMinor ?? 0) / 100)
}

/**
 * When a coupon expires: its date and time in the viewer's time zone, named, such as "Dec 31, 2026,
 * 11:59 PM EST". Midnight shows too ("12:00 AM"), because the coupon ends as that day starts. The
 * fields are spelled out: `dateStyle` does not combine with `timeZoneName`.
 */
function expiry(value: string | null) {
  if (!value) return "—"
  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    timeZoneName: "short",
    year: "numeric",
  }).format(new Date(value))
}

function duration(coupon: Pick<AdminCoupon, "duration" | "durationMonths">) {
  if (coupon.duration === "repeating") {
    return coupon.durationMonths === 1 ? "1 month" : `${coupon.durationMonths} months`
  }
  return coupon.duration === "once" ? "Once" : "Forever"
}

/**
 * What Create coupon's wizard edits. Numbers stay as typed until the request is built, so a field
 * can be empty while it is edited. The code, the limit and the end date are optional: blank sends
 * null, as the one-page form did with their checkbox off, and no products means every product.
 */
export type CouponDraft = {
  name: string
  type: "percent" | "fixed"
  percentOff: string
  amountOff: string
  currency: string
  duration: AdminCreateCouponRequest["duration"]
  durationMonths: string
  code: string
  maxRedemptions: string
  redeemBy: string
  productIds: string[]
}

// The one-page form's defaults.
const NEW_COUPON: CouponDraft = {
  amountOff: "10",
  code: "",
  currency: "USD",
  duration: "forever",
  durationMonths: "1",
  maxRedemptions: "",
  name: "",
  percentOff: "25",
  productIds: [],
  redeemBy: "",
  type: "percent",
}

const couponFields = adminCreateCouponRequestSchema.shape

/** The request, its keys in the order the one-page form sent them. */
function couponRequest(draft: CouponDraft): AdminCreateCouponRequest {
  return {
    amountOffMinor: draft.type === "fixed" ? Math.round(Number(draft.amountOff) * 100) : null,
    appliesToProductIds: draft.productIds,
    code: draft.code.trim() ? draft.code : null,
    currency: draft.currency,
    duration: draft.duration,
    durationMonths: draft.duration === "repeating" ? Number(draft.durationMonths) : null,
    maxRedemptions: draft.maxRedemptions.trim() ? Number(draft.maxRedemptions) : null,
    name: draft.name,
    percentOff: draft.type === "percent" ? Number(draft.percentOff) : null,
    redeemBy: draft.redeemBy ? new Date(draft.redeemBy).toISOString() : null,
    type: draft.type,
  }
}

/** True when `text` is a number from `min` to `max`, and a whole one when `whole`. */
function inRange(text: string, min: number, max: number, whole = false) {
  const value = Number(text)
  return text.trim() !== "" && value >= min && value <= max && (!whole || Number.isInteger(value))
}

function discountErrors(draft: CouponDraft): WizardErrors {
  const errors: WizardErrors = {}
  if (!couponFields.name.safeParse(draft.name).success) {
    errors.name = "Enter a name for the coupon."
  }
  if (draft.type === "percent" && !inRange(draft.percentOff, 0.01, 100)) {
    errors.percentOff = "Enter a percentage from 0.01 to 100."
  }
  if (draft.type === "fixed" && !inRange(draft.amountOff, 0.01, Number.MAX_SAFE_INTEGER / 100)) {
    errors.amountOff = "Enter an amount of at least 0.01."
  }
  if (draft.duration === "repeating" && !inRange(draft.durationMonths, 1, 36, true)) {
    errors.durationMonths = "Enter a whole number of months from 1 to 36."
  }
  return errors
}

function limitErrors(draft: CouponDraft): WizardErrors {
  const errors: WizardErrors = {}
  const code = draft.code.trim()
  if (code && !couponFields.code.safeParse(draft.code).success) {
    // A space is the usual mistake, so it is named first.
    if (/\s/.test(code)) {
      errors.code =
        "Codes can’t contain spaces. Use letters, numbers, hyphens (-) or underscores (_)."
    } else if (code.length > 40) errors.code = "Use 40 characters or fewer."
    else errors.code = "Use only letters, numbers, hyphens (-) or underscores (_)."
  }
  if (
    draft.maxRedemptions.trim() &&
    !inRange(draft.maxRedemptions, 1, Number.MAX_SAFE_INTEGER, true)
  ) {
    errors.maxRedemptions = "Enter a whole number of at least 1, or leave it blank."
  }
  return errors
}

function DiscountStep({ draft, errors, setDraft }: WizardStepContext<CouponDraft>) {
  const shown = useErrorsUntilEdited(errors, draft)
  return (
    <>
      {/* Side by side where they fit, so the step fits a 720px screen with its errors. */}
      <div className="admin-field-grid">
        <Field error={shown.name} label="Name" required>
          <Input
            onChange={(event) => setDraft({ name: event.target.value })}
            placeholder="Spring sale"
            value={draft.name}
          />
        </Field>
        <fieldset className="admin-fieldset">
          <legend className="tny-label">Discount</legend>
          <div className="admin-field-pair">
            <Radio
              checked={draft.type === "percent"}
              label="Percentage"
              name="coupon-type"
              onChange={() => setDraft({ type: "percent" })}
            />
            <Radio
              checked={draft.type === "fixed"}
              label="Fixed amount"
              name="coupon-type"
              onChange={() => setDraft({ type: "fixed" })}
            />
          </div>
        </fieldset>
      </div>
      {draft.type === "percent" ? (
        <Field error={shown.percentOff} label="Percent off" required>
          <Input
            inputMode="decimal"
            max="100"
            min="0.01"
            onChange={(event) => setDraft({ percentOff: event.target.value })}
            size="short"
            step=".01"
            suffix="%"
            type="number"
            value={draft.percentOff}
          />
        </Field>
      ) : (
        // A percentage has no currency, so the currency shows only beside an amount.
        <div className="admin-field-pair">
          <Field error={shown.amountOff} label="Amount off" required>
            <Input
              inputMode="decimal"
              min="0.01"
              onChange={(event) => setDraft({ amountOff: event.target.value })}
              prefix={currencySymbol(draft.currency)}
              size="short"
              step=".01"
              type="number"
              value={draft.amountOff}
            />
          </Field>
          <Field label="Currency">
            <Select
              onChange={(event) => setDraft({ currency: event.target.value })}
              value={draft.currency}
            >
              {supportedCurrencies().map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      )}
      <div className="admin-field-pair">
        <Field label="Duration">
          <Select
            onChange={(event) =>
              setDraft({ duration: event.target.value as CouponDraft["duration"] })
            }
            value={draft.duration}
          >
            <option value="forever">Forever</option>
            <option value="once">Once</option>
            <option value="repeating">Multiple months</option>
          </Select>
        </Field>
        {draft.duration === "repeating" ? (
          <Field error={shown.durationMonths} label="Number of months" required>
            <Input
              inputMode="numeric"
              max="36"
              min="1"
              onChange={(event) => setDraft({ durationMonths: event.target.value })}
              type="number"
              value={draft.durationMonths}
            />
          </Field>
        ) : null}
      </div>
    </>
  )
}

function LimitsStep({
  catalog,
  draft,
  errors,
  setDraft,
}: WizardStepContext<CouponDraft> & { catalog: AdminCouponCatalog }) {
  const shown = useErrorsUntilEdited(errors, draft)
  return (
    <div className="admin-setup-fields">
      <Field
        error={shown.code}
        hint={shown.code ? undefined : "Leave it blank for a generated code."}
        label="Code"
      >
        <Input
          className="admin-mono"
          onChange={(event) => setDraft({ code: event.target.value })}
          placeholder="GRANDLAUNCH"
          value={draft.code}
        />
      </Field>
      <MultiSelect
        // None chosen is every product, as the request reads an empty list: a value, not a prompt.
        emptyLabel="All products"
        label="Products"
        onChange={(productIds) => setDraft({ productIds })}
        options={catalog.products
          .filter((product) => product.stock > 0)
          .map((product) => ({ label: product.name, value: product.productId }))}
        value={draft.productIds}
      />
      <Field
        error={shown.maxRedemptions}
        hint={shown.maxRedemptions ? undefined : "Leave it blank for no limit."}
        label="Maximum redemptions"
      >
        <Input
          inputMode="numeric"
          min="1"
          onChange={(event) => setDraft({ maxRedemptions: event.target.value })}
          type="number"
          value={draft.maxRedemptions}
        />
      </Field>
      <Field hint="Leave it blank to never end." label="Expires">
        <Input
          onChange={(event) => setDraft({ redeemBy: event.target.value })}
          type="datetime-local"
          value={draft.redeemBy}
        />
      </Field>
    </div>
  )
}

/** Every value the coupon is created with, each with an Edit that opens its step. */
function ReviewStep({
  catalog,
  draft,
  goTo,
}: WizardStepContext<CouponDraft> & { catalog: AdminCouponCatalog }) {
  const coupon = couponRequest(draft)
  const names = new Map(catalog.products.map((product) => [product.productId, product.name]))
  const rows = [
    { label: "Name", step: "discount", value: coupon.name },
    { label: "Discount", step: "discount", value: `${discount(coupon)} off` },
    { label: "Duration", step: "discount", value: duration(coupon) },
    {
      label: "Code",
      step: "limits",
      value: coupon.code ? (
        <span className="admin-mono">{coupon.code}</span>
      ) : (
        "Generated automatically"
      ),
    },
    {
      label: "Products",
      step: "limits",
      value:
        coupon.appliesToProductIds.length > 0
          ? coupon.appliesToProductIds.map((id) => names.get(id) ?? id).join(", ")
          : "All products",
    },
    {
      label: "Maximum redemptions",
      step: "limits",
      value: coupon.maxRedemptions === null ? "No limit" : String(coupon.maxRedemptions),
    },
    {
      label: "Expires",
      step: "limits",
      value: coupon.redeemBy ? expiry(coupon.redeemBy) : "Never",
    },
  ]
  return (
    <dl className="admin-review">
      {rows.map((row) => (
        <div className="admin-review-row" key={row.label}>
          <dt className="admin-review-term">{row.label}</dt>
          <dd className="admin-review-value">{row.value}</dd>
          <dd className="admin-review-edit">
            <Button onClick={() => goTo(row.step)} size="sm" variant="ghost">
              {/* The space is outside the hidden text, where a name keeps it. */}
              {"Edit "}
              <span className="tny-visually-hidden">{row.label}</span>
            </Button>
          </dd>
        </div>
      ))}
    </dl>
  )
}

/** The wizard, once the catalog it offers products from has loaded. */
function CreateCouponWizard({
  initialDraft,
  onClose,
  stepId,
}: {
  initialDraft: Partial<CouponDraft> | undefined
  onClose(): void
  stepId: string | undefined
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const query = useQuery({ queryFn: loadAdminCoupons, queryKey: ["admin-coupons"] })
  const catalog = query.data
  if (!catalog) {
    return query.isError ? (
      <LoadError
        detail={errorDetail(query.error)}
        message="We couldn't load your coupons"
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    ) : (
      <p className="admin-note" role="status">
        Loading…
      </p>
    )
  }
  const steps: WizardStep<CouponDraft>[] = [
    {
      fields: ["name", "type", "percentOff", "amountOff", "currency", "duration", "durationMonths"],
      id: "discount",
      render: (context) => <DiscountStep {...context} />,
      title: "Discount",
      validate: discountErrors,
    },
    {
      fields: ["code", "productIds", "maxRedemptions", "redeemBy"],
      id: "limits",
      render: (context) => <LimitsStep catalog={catalog} {...context} />,
      title: "Code and limits",
      validate: limitErrors,
    },
    {
      id: "review",
      render: (context) => <ReviewStep catalog={catalog} {...context} />,
      title: "Review",
    },
  ]
  return (
    <FormWizard<CouponDraft>
      headingLevel={2}
      initialDraft={{ ...NEW_COUPON, ...initialDraft }}
      onSubmit={async (draft) => {
        try {
          await createAdminCoupon(couponRequest(draft))
        } catch (error) {
          // A taken code goes back to the field that owns it (admin-api's words for Stripe's
          // resource_already_exists); any other refusal stays on Review.
          if (
            draft.code.trim() &&
            error instanceof Error &&
            /code already exists/i.test(error.message)
          ) {
            throw new WizardFieldError("limits", { code: "That code already exists." })
          }
          throw error
        }
        await queryClient.invalidateQueries({ queryKey: ["admin-coupons"] })
        toast({ message: `${draft.name} created.`, tone: "success" })
        onClose()
      }}
      steps={steps}
      stepId={stepId}
      submitLabel="Create coupon"
      title="Coupon details"
    />
  )
}

/**
 * Create coupon, in three steps so none scrolls: Discount (the name, a percentage or an amount in
 * a currency, and how long it lasts), Code and limits, then Review. Create sends the same body the
 * one-page form did. `stepId` and `initialDraft` open a given step, as the stories do.
 */
export function CreateCouponDialog({
  initialDraft,
  onClose,
  open,
  stepId,
}: {
  open: boolean
  onClose(): void
  stepId?: string | undefined
  initialDraft?: Partial<CouponDraft> | undefined
}): ReactElement {
  return (
    <Dialog onClose={onClose} open={open} size="lg" title="Create coupon">
      <CreateCouponWizard initialDraft={initialDraft} onClose={onClose} stepId={stepId} />
    </Dialog>
  )
}

/**
 * The Stripe coupons. Create coupon opens the create form; each row's Delete asks in a
 * ConfirmDialog first.
 */
export function AdminCouponsPage() {
  const queryClient = useQueryClient()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [search, setSearch] = useState("")
  const [showForm, setShowForm] = useState(false)
  const [deleting, setDeleting] = useState<AdminCoupon | null>(null)
  const [notice, setNotice] = useState("")
  const [actionError, setActionError] = useState("")
  const catalogQuery = useQuery({
    queryFn: loadAdminCoupons,
    queryKey: ["admin-coupons"],
  })
  const catalog = catalogQuery.data
  const remove = useMutation({
    mutationFn: (coupon: AdminCoupon) => deleteAdminCoupon(coupon.id),
    async onSuccess(_data, coupon) {
      setDeleting(null)
      setNotice(`${coupon.name} deleted.`)
      await queryClient.invalidateQueries({ queryKey: ["admin-coupons"] })
      // The row's Actions button left with the row, so focus goes to the page heading.
      headingRef.current?.focus()
    },
    onError(error) {
      setDeleting(null)
      setActionError(error instanceof Error ? error.message : "Coupon could not be deleted.")
    },
  })

  const writable = Boolean(catalog?.writable)
  const coupons = catalog?.coupons.filter((coupon) => couponMatches(coupon, search)) ?? []
  const productNames = new Map(
    catalog?.products.map((product) => [product.productId, product.name]) ?? [],
  )

  function openForm() {
    setNotice("")
    setActionError("")
    setShowForm(true)
  }

  // Disabled until the list has loaded, so it holds its place; a list that cannot load offers none.
  const addButton = (
    <Button disabled={!catalog || !writable} onClick={openForm}>
      Create coupon
    </Button>
  )

  const columns: Column<AdminCoupon>[] = [
    { cell: (coupon) => coupon.name, header: "Name", key: "name" },
    {
      cell: (coupon) => <code className="admin-mono">{coupon.id}</code>,
      header: "Code",
      key: "code",
    },
    { cell: discount, header: "Discount", key: "discount" },
    {
      align: "end",
      cell: (coupon) =>
        coupon.maxRedemptions
          ? `${coupon.redemptions}/${coupon.maxRedemptions}`
          : String(coupon.redemptions),
      header: "Redemptions",
      key: "redemptions",
      priority: 2,
    },
    { cell: (coupon) => expiry(coupon.redeemBy), header: "Expires", key: "expiry", priority: 2 },
    { cell: duration, header: "Duration", key: "duration", priority: 2 },
    {
      cell: (coupon) =>
        coupon.appliesToProductIds.length > 0
          ? coupon.appliesToProductIds.map((id) => productNames.get(id) ?? id).join(", ")
          : "All products",
      header: "Products",
      key: "products",
      priority: 3,
    },
    {
      align: "end",
      // The row's one action, so a button of its own rather than a menu of one.
      cell: (coupon) => (
        <IconButton
          className="admin-row-delete"
          disabled={!writable}
          icon={<NavIcon name="trash" />}
          label={`Delete ${coupon.name}`}
          onClick={(event) => {
            // Focused, so the ConfirmDialog returns there even where a click does not focus a
            // button (Safari).
            event.currentTarget.focus()
            setNotice("")
            setActionError("")
            setDeleting(coupon)
          }}
        />
      ),
      header: "Actions",
      key: "actions",
    },
  ]

  return (
    <section className="admin-stack admin-page">
      <PageHeader
        actions={
          <>
            <DocsLink />
            <PaymentSettingsLink />
            {catalogQuery.isError || catalog?.coupons.length === 0 ? null : addButton}
          </>
        }
        description="Discounts members enter at checkout."
        headingRef={headingRef}
        title="Coupons"
      />
      <CommerceTabs active="coupons" />
      {catalogQuery.isError ? (
        <LoadError
          detail={errorDetail(catalogQuery.error)}
          message="We couldn't load your coupons"
          onRetry={() => void catalogQuery.refetch()}
          retrying={catalogQuery.isFetching}
        />
      ) : (
        <>
          {catalog && !writable ? (
            <p className="admin-note admin-inline-links">
              Coupons need a connected Stripe account.{" "}
              <Link to="/dashboard/stripe">Connect Stripe</Link>
            </p>
          ) : null}
          {actionError ? (
            <p className="tny-error" role="alert">
              {actionError}
            </p>
          ) : null}
          <p className="admin-note" role="status">
            {notice}
          </p>
          {catalog && catalog.coupons.length === 0 ? (
            <EmptyState
              action={writable ? addButton : undefined}
              description="A coupon gives members a discount at checkout, with its own code or on the products you choose."
              title="Create your first coupon"
            />
          ) : (
            <>
              {/* While the list loads the box holds its place, disabled. */}
              <div className="admin-toolbar">
                <SearchInput
                  disabled={!catalog}
                  label="Search coupons"
                  onSearch={setSearch}
                  value={search}
                />
              </div>
              <Table
                caption="Coupons"
                captionHidden
                columns={columns}
                empty={<p className="admin-note">No coupons match “{search.trim()}”.</p>}
                loading={catalogQuery.isPending}
                loadingLabel="Loading coupons…"
                rowKey={(coupon) => coupon.id}
                rows={coupons}
              />
            </>
          )}
        </>
      )}
      <CreateCouponDialog onClose={() => setShowForm(false)} open={showForm} />
      <ConfirmDialog
        body="Members can no longer redeem it. Discounts it already gave stay in place."
        confirmLabel="Delete coupon"
        danger
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting)
        }}
        open={deleting !== null}
        pending={remove.isPending}
        title={deleting ? `Delete “${deleting.name}”?` : "Delete this coupon?"}
      />
    </section>
  )
}
